import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation, useSearchParams } from 'react-router-dom';

import agenciesReadReq from '@careerstart/wae-common/schema/agency/read.post.req.json';
import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Box } from '@mui/material';

import AgencyCard from '../../../components/AgencyCard/AgencyCard';
import { validateSchema } from '../../../components/Form/validations';
import {
  selectAgencies,
  selectAgencyTotalCount,
  selectIsAgenciesLoading,
} from '../../../store/selectors/dashboardSelector';
import useFairReliabilityScore from '../../../utils/useFairReliabilityScore';
import { ROUTE_CONSTANTS } from '../../app/RouteBuilding/routeConstants';
import { getFillRate } from '../dashboardHelpers';
import { getAgenciesForDashboard } from '../dashboardReducer';

import AgenciesView from './AgenciesView';
import AgencyFilters from './AgencyFilters';
import { AGENCY_SEARCH_PARAM_KEYS } from './dashboardAgencyConstatnts';

const LIMIT = 5;

const INITIAL_FILTERS = {
  filters: [],
  limit: LIMIT,
  page: 0,
};

const Agencies = () => {
  const previousFiltersRef = useRef();
  const sentinelRef = useRef(null);
  const location = useLocation();
  const dispatch = useDispatch();
  const { pickReliability } = useFairReliabilityScore();

  const agencyDetailPath = (agencyId) =>
    agencyId
      ? `${generatePath(`/${ROUTE_CONSTANTS.DASHBOARD_AGENCY}/:id`, {
          id: agencyId,
        })}${location.search}`
      : undefined;

  const isLoading = useSelector(selectIsAgenciesLoading);
  const agencies = useSelector(selectAgencies);
  const agencyTotalCount = useSelector(selectAgencyTotalCount);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterAndSortData, setFilterAndSortData] = useState(INITIAL_FILTERS);

  const handleOnNameSearchChange = useCallback(
    (newPositionFilter) => {
      const agencySearchTerm = newPositionFilter?.[0]?.value;
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!agencySearchTerm) {
        delete newParams[AGENCY_SEARCH_PARAM_KEYS.AGENCY];
      } else {
        newParams[AGENCY_SEARCH_PARAM_KEYS.AGENCY] = agencySearchTerm;
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );
  useEffect(() => {
    if (isLoading) return;
    if (!isEqual(previousFiltersRef.current, filterAndSortData)) {
      previousFiltersRef.current = filterAndSortData;
      if (Object.keys(validateSchema(filterAndSortData, agenciesReadReq)).length === 0) {
        dispatch(getAgenciesForDashboard(filterAndSortData));
      }
    }
  }, [dispatch, filterAndSortData, isLoading]);

  useEffect(() => {
    setFilterAndSortData((prev) => {
      const newFilter = searchParams.get(AGENCY_SEARCH_PARAM_KEYS.AGENCY)
        ? [
            {
              field: 'name',
              operation: 'icontains',
              value: searchParams.get(AGENCY_SEARCH_PARAM_KEYS.AGENCY),
            },
          ]
        : [];

      const filters = {
        filters: newFilter,
        limit: LIMIT,
        page: 0,
      };

      if (isEqual(prev?.filters, filters)) {
        return prev;
      }
      return filters;
    });
  }, [searchParams]);

  const handleIntersection = useCallback(
    (entries) => {
      if (entries?.[0].isIntersecting && agencyTotalCount > agencies?.length && !isLoading) {
        setFilterAndSortData((prevVal) => ({
          ...prevVal,
          page: prevVal.page + 1,
        }));
      }
    },
    [agencies, agencyTotalCount, isLoading]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    const currentSentinel = sentinelRef.current;

    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [handleIntersection]);

  return (
    <Box>
      <AgenciesView
        isLoading={isLoading}
        content={agencies?.map((agency) => (
          <AgencyCard
            to={agencyDetailPath(agency._id)}
            key={agency?.onboardingId}
            agencyData={{
              activeWorkers: agency?.dashboardData?.workers?.active,
              name: agency?.name,
              onboardingID: agency?.onboardingId,
              averageReliabilityScore: pickReliability(
                agency?.dashboardData?.workers?.averageRawReliability,
                agency?.dashboardData?.workers?.averageReliabilityScore
              ),
              availableRevenue: agency?.dashboardData?.revenue?.unscheduled,
              scheduledRevenue: agency?.dashboardData?.revenue?.scheduled,
              openings: agency?.dashboardData?.futurePlacementsData?.totalOpenings,
              placements: agency?.dashboardData?.futurePlacementsData?.totalActivePlacements,
              fillRate: getFillRate(
                agency?.dashboardData?.futurePlacementsData?.totalActivePlacements,
                agency?.dashboardData?.futurePlacementsData?.totalOpenings
              ),

              location:
                agency?.address?.city && agency?.address?.state
                  ? `${agency?.address?.city}, ${agency?.address?.state}`
                  : '--',
            }}
          />
        ))}
        filters={
          <AgencyFilters
            isLoading={isLoading}
            handleOnNameSearchChange={handleOnNameSearchChange}
            initialValues={{ value: searchParams.get(AGENCY_SEARCH_PARAM_KEYS.AGENCY) }}
          />
        }
        title={LanguageConverter('dashboard.agencies.title')}
      />

      <div ref={sentinelRef} />
    </Box>
  );
};

export default Agencies;
